import { useTranslation } from 'react-i18next'

import { Field, Select } from '@/components/ui/Field'
import { toOptions } from '@/components/ui/control'
import { FormSheet } from '@/components/ui/FormSheet'
import { Input } from '@/components/ui/Input'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { refusalMessage } from '@/features/cases/lib/refusal'
import {
  useAmendPayment,
  useIssuingEntityOptions,
  type PaymentAmendPayload,
} from '@/features/finance/api/useFinance'
import { CounterpartySelect } from '@/features/finance/components/CounterpartySelect'
import { normaliseDecimal } from '@/features/finance/lines'
import { useFormat } from '@/i18n/useFormat'
import type { Payment, PaymentMethod } from '@/types/domain'

/* Correcting a PENDING payment in place — the third fate beside confirm and
 * reject, for the row that records the right money on the right day and
 * mistypes a field of it.
 *
 * WHAT THE FORM DOES NOT OFFER, and why the absence is the design: the
 * currency, the received-on date, the direction and the case are not fields
 * here, because the wire does not carry them (`PaymentAmendPayload`) and the
 * server has no column-move to make (`PaymentAmendment`). The first two are
 * what every booking on the row and its allocations hangs off — amending
 * either would strand settlement figures booked at a date the payment no
 * longer states — so a payment recorded on the wrong day or in the wrong
 * currency is REJECTED with a reason and re-recorded, which re-books
 * everything by construction. The sheet says so in one line over the fields,
 * so the reader is told where the door is rather than left hunting for a
 * disabled box.
 *
 * The AMOUNT may move, but not below what the payment has already settled —
 * the server's below-allocated refusal (AD-11 asked of an edit) comes back on
 * the amount field like any other field problem.
 */

const METHODS: PaymentMethod[] = ['cash', 'bank_transfer']

const CURRENCIES = ['USD', 'EUR', 'CNY', 'RUB', 'TMT'] as const

function isValidAmount(raw: string): boolean {
  const normalised = normaliseDecimal(raw)
  return normalised !== '' && /^\d+(\.\d{1,2})?$/.test(normalised)
}

interface AmendDraft {
  reference: string
  counterpartyName: string
  method: PaymentMethod
  issuingEntityId: string
  amount: string
  feeAmount: string
  feeCurrency: string
}

export function PaymentAmendSheet({
  open,
  onClose,
  payment,
}: {
  open: boolean
  onClose: () => void
  payment: Payment
}) {
  const { t } = useTranslation()
  const format = useFormat()
  const { data: entities } = useIssuingEntityOptions()
  const amend = useAmendPayment()

  const [draft, setDraft, touched, markTouched, dirty] = useFormDraft<AmendDraft>(
    open ? payment.id : null,
    () => ({
      reference: payment.reference,
      counterpartyName: payment.counterpartyName,
      method: payment.method,
      issuingEntityId: payment.issuingEntityId,
      amount: payment.amount.amount,
      // Absent stays absent: seeding '0.00' would turn "no fee was recorded"
      // into a claim the row never made (PaymentFormSheet's own rule).
      feeAmount: payment.fee?.amount ?? '',
      feeCurrency: payment.fee?.currency ?? '',
    }),
  )

  const referenceError =
    touched && !draft.reference.trim() ? t('common.form.required') : undefined
  const counterpartyError =
    touched && !draft.counterpartyName.trim() ? t('common.form.required') : undefined
  const entityError =
    touched && !draft.issuingEntityId ? t('common.form.required') : undefined
  const amountError =
    touched && !isValidAmount(draft.amount) ? t('common.form.invalidNumber') : undefined
  const feeTyped = draft.feeAmount.trim() !== ''
  const feeError =
    touched && feeTyped && !isValidAmount(draft.feeAmount)
      ? t('common.form.invalidNumber')
      : undefined

  const invalid =
    !draft.reference.trim() ||
    !draft.counterpartyName.trim() ||
    !draft.issuingEntityId ||
    !isValidAmount(draft.amount) ||
    (feeTyped && !isValidAmount(draft.feeAmount))

  const submit = () => {
    markTouched()
    if (invalid) return

    const payload: PaymentAmendPayload = {
      reference: draft.reference.trim(),
      counterpartyName: draft.counterpartyName.trim(),
      method: draft.method,
      issuingEntityId: draft.issuingEntityId,
      amount: normaliseDecimal(draft.amount),
      ...(feeTyped
        ? {
            fee: {
              amount: normaliseDecimal(draft.feeAmount),
              currency:
                draft.feeCurrency === ''
                  ? payment.amount.currency
                  : draft.feeCurrency,
            },
          }
        : {}),
    }

    amend.mutate(
      { paymentId: payment.id, caseId: payment.caseId, payload },
      { onSuccess: onClose },
    )
  }

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={t('finance.payments.amendTitle', { reference: payment.reference })}
      submitLabel={t('finance.payments.amendSubmit')}
      pending={amend.isPending}
      dirty={dirty}
      error={
        amend.error
          ? refusalMessage(amend.error, t)
          : touched && invalid
            ? t('common.form.fixFields')
            : undefined
      }
      onSubmit={submit}
    >
      {/* The facts this form cannot move, stated as a sentence rather than as
          four disabled boxes — and where the door is when one of THEM is the
          typo. */}
      <p className="text-text-muted text-[12.5px] leading-snug font-light">
        {t('finance.payments.amendFixedFacts', {
          currency: payment.amount.currency,
          date: format.dateShort(payment.receivedOn),
        })}
      </p>

      <Field label={t('finance.payments.reference')} required error={referenceError}>
        {(props) => (
          <Input
            value={draft.reference}
            onChange={(e) => setDraft({ ...draft, reference: e.target.value })}
            {...props}
          />
        )}
      </Field>

      <Field
        label={t('finance.balances.counterparty')}
        required
        error={counterpartyError}
        hint={t('finance.payments.counterpartyHint')}
      >
        {(props) => (
          <CounterpartySelect
            value={draft.counterpartyName}
            onChange={(counterpartyName) => setDraft({ ...draft, counterpartyName })}
            {...props}
          />
        )}
      </Field>

      <div className="flex gap-2.5">
        <Field label={t('finance.payments.method')} required className="flex-1">
          {(props) => (
            <Select
              value={draft.method}
              onChange={(method) => setDraft({ ...draft, method })}
              options={toOptions(METHODS, (method) =>
                t(`finance.payments.methods.${method}`),
              )}
              {...props}
            />
          )}
        </Field>

        {/* The currency travels beside the amount but is NOT a control: it is
            the fact the fixed-facts line names, rendered where the reader's eye
            checks it against the figure. */}
        <Field
          label={t('common.fields.amount')}
          required
          error={amountError}
          hint={payment.amount.currency}
          className="flex-1"
        >
          {(props) => (
            <Input
              inputMode="decimal"
              value={draft.amount}
              onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
              className="asa-num"
              {...props}
            />
          )}
        </Field>
      </div>

      <Field
        label={t('finance.payments.entity')}
        required
        error={entityError}
        hint={t('finance.payments.entityHint')}
      >
        {(props) => (
          <Select
            value={draft.issuingEntityId}
            onChange={(issuingEntityId) => setDraft({ ...draft, issuingEntityId })}
            options={(entities ?? []).map((entity) => ({
              value: entity.id,
              label: entity.name,
            }))}
            {...props}
          />
        )}
      </Field>

      {/* The fee replaces the stored pair WHOLESALE: the boxes show what the
          row holds and what the reader leaves in them is the truth — an
          emptied pair means "no fee", not "no change". Its currency IS
          amendable, unlike the payment's: a commission is re-read off the
          statement in whatever the bank charged. */}
      <div className="border-line-hair flex flex-col gap-2 border-t pt-3">
        <span className="asa-eyebrow">{t('finance.payments.feeHeading')}</span>

        <div className="flex gap-2.5">
          <Field
            label={t('finance.payments.fee')}
            error={feeError}
            hint={t('finance.payments.feeHint')}
            className="flex-1"
          >
            {(props) => (
              <Input
                inputMode="decimal"
                value={draft.feeAmount}
                onChange={(e) => setDraft({ ...draft, feeAmount: e.target.value })}
                className="asa-num"
                {...props}
              />
            )}
          </Field>

          <Field label={t('finance.payments.feeCurrency')} className="w-28 shrink-0">
            {(props) => (
              <Select
                value={
                  draft.feeCurrency === '' ? payment.amount.currency : draft.feeCurrency
                }
                onChange={(feeCurrency) => setDraft({ ...draft, feeCurrency })}
                options={toOptions(CURRENCIES, (currency) => currency)}
                {...props}
              />
            )}
          </Field>
        </div>
      </div>
    </FormSheet>
  )
}
